import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import type { SolarContractReadinessDto } from "../dto/solar-contract.dto"
import { solarProposalService } from "../services/solar-proposal.service"

export type SendSolarContractVariables = {
  proposalId: string
  idempotencyKey: string
}

/**
 * Send documents for a Solar Proposal. The answer is the readiness read
 * itself. Every other read of the proposal refetches: the header for its
 * contract status, and each step for the financing lock a package in flight
 * holds. A failure re-reads readiness too, since a superseded or reconciling
 * attempt moved it, and an uncertain send locks as well.
 */
export default function useSendSolarContract(): UseMutationResult<
  SolarContractReadinessDto,
  unknown,
  SendSolarContractVariables
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId, idempotencyKey }: SendSolarContractVariables) =>
      solarProposalService.sendContract(proposalId, { idempotencyKey }),
    onSuccess: (readiness, { proposalId }) => {
      queryClient.setQueryData(
        queryKeys.solarProposal.contract(proposalId),
        readiness,
      )
      const contractKey = queryKeys.solarProposal.contract(proposalId)
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.byId(proposalId),
        predicate: (query) => query.queryKey[2] !== contractKey[2],
      })
    },
    onError: (_error, { proposalId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.byId(proposalId),
      })
    },
  })
}
